import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Dialog, DialogContent } from '@duck/ui'
import { useTar } from '@/modules'
import { LoginView } from '../LoginDialog/LoginView'
import { SignupView } from '../SignupDialog/SignupView'

type AuthMode = 'login' | 'signup'

/**
 * Decorative "lab console" atmosphere painted behind both auth views: a dot
 * grid, a teal glow pooling at the bottom, fine grain, HUD corner brackets and
 * a one-shot scan-line on open. Purely visual — pointer-events-none, aria-hidden,
 * and clipped to the dialog's own rounded shape via rounded-[inherit].
 */
const AuthAtmosphere = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[inherit]"
  >
    {/* subtle teal glow pooling at the bottom (continuity with the page glow) */}
    <div
      className="absolute inset-0"
      style={{
        background:
          'radial-gradient(75% 55% at 50% 112%, hsl(var(--color-accent) / 0.06), transparent 70%)',
      }}
    />
    {/* fine grain */}
    <svg className="absolute inset-0 h-full w-full opacity-[0.15] mix-blend-overlay">
      <filter id="auth-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" />
      </filter>
      <rect width="100%" height="100%" filter="url(#auth-grain)" />
    </svg>
    {/* one-shot scan-line on open */}
    <div className="absolute inset-x-3 top-0 h-px overflow-hidden">
      <div className="h-px w-full animate-auth-scan bg-gradient-to-r from-transparent via-accent to-transparent" />
    </div>
  </div>
)

/** Shared XP Lab monogram + wordmark lockup shown above both auth views. */
const AuthBrandLockup = () => {
  const { t } = useTar()
  return (
    // pt-4 gives the monogram's glow room to clear the dialog body's scroll
    // container (overflow-y-auto clips all sides; only the horizontal axis has
    // breathing room via px-2/-mx-2 on the shared DialogContent).
    <div className="mb-5 flex items-center gap-3 pt-4">
      <div
        className="grid h-[38px] w-[38px] place-items-center rounded-xl border border-accent/40 font-bruno-ace-sc text-[13px] uppercase tracking-wide text-accent"
        style={{
          background:
            'radial-gradient(120% 120% at 30% 20%, hsl(var(--color-accent) / 0.22), hsl(var(--color-card)))',
          boxShadow:
            '0 0 18px hsl(var(--color-accent) / 0.25), inset 0 1px 0 hsl(var(--color-card-foreground) / 0.06)',
        }}
      >
        XP
      </div>
      <span className="font-bruno-ace-sc text-[13px] uppercase tracking-[1.5px] text-foreground">
        {t('common.appName')}
      </span>
    </div>
  )
}

/**
 * Single shared shell for the login and signup flows. Both flows render inside
 * one Radix Dialog whose `open` stays true while switching between them, so
 * there is no close/reopen flash — only the inner body cross-fades. The two
 * underlying atoms (isLoginDialogOpen / isSignupDialogOpen) are unchanged; this
 * just merges their presentation.
 */
export const AuthDialog = () => {
  const {
    isLoginDialogOpen,
    isSignupDialogOpen,
    closeLoginDialog,
    closeSignupDialog,
  } = useAuth()

  const [isBusy, setIsBusy] = useState(false)

  // Remember the active mode so that while the dialog animates closed (both
  // atoms false) we keep showing the view the user was on, instead of snapping
  // back to the login view mid-fade-out.
  const [mode, setMode] = useState<AuthMode>('login')
  useEffect(() => {
    if (isSignupDialogOpen) setMode('signup')
    else if (isLoginDialogOpen) setMode('login')
  }, [isLoginDialogOpen, isSignupDialogOpen])

  const isOpen = isLoginDialogOpen || isSignupDialogOpen

  const handleOpenChange = (open: boolean) => {
    if (open || isBusy) return
    closeLoginDialog()
    closeSignupDialog()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-full h-full sm:w-auto sm:max-w-md rounded-none sm:rounded-lg sm:min-w-[480px]"
        style={{ backgroundColor: 'hsl(var(--color-auth-surface))' }}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerDownOutside={(e) => isBusy && e.preventDefault()}
        onEscapeKeyDown={(e) => isBusy && e.preventDefault()}
        onInteractOutside={(e) => isBusy && e.preventDefault()}
      >
        <AuthAtmosphere />

        {/* Static brand lockup stays put while the body slides between modes. */}
        <div className="relative z-10">
          <AuthBrandLockup />

          {/* Static clipper: confines the sliding panel's off-screen travel so it
              never spills past the dialog edge or flashes a horizontal scrollbar
              on the body's overflow-y-auto container. */}
          <div className="overflow-hidden">
            {/* key={mode} remounts on switch so the enter animation replays.
                Signup slides in from the right (forward), login from the left
                (back). Tooltips portal to <body>, so clipping doesn't touch them. */}
            <div
              key={mode}
              className={`animate-in fade-in-0 duration-300 ${
                mode === 'signup' ? 'slide-in-from-right-10' : 'slide-in-from-left-10'
              }`}
            >
              {mode === 'signup' ? (
                <SignupView onBusyChange={setIsBusy} />
              ) : (
                <LoginView onBusyChange={setIsBusy} />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
